import headsUp from 'headsup.js';
import scrollLock from 'scroll-lock';

class Reader {
  /**
   * Initialize a Reader. This JavaScript is primarily responsible for
   * binding event listeners so you can open and close the search menu and for
   * the "peekaboo" navigation that moves as you scroll.
   *
   * @param {Element} element - Your Reader! An element with markup matching
   *  our Reader component pattern.
   */
  constructor(element) {
    this.element = element;
    this.mastheadSelector = '.js-Reader-masthead';
    this.mastheadHiddenClass = 'is-readerHiddenMasthead';
    this.fullNavShownClass = 'is-fullNavShown';
    this.searchShownClass = 'is-searchShown';
    this.iconToggleClass = 'is-on';
    this.moreToggle = this.element.querySelector('.js-Reader-moreToggle');
    this.fullNav = this.element.querySelector('.js-Reader-fullNav');
    this.quickNavLinks = this.element.querySelectorAll(
      '.Reader-quickNavAction',
    );
    this.overlay = this.element.querySelector('.js-Reader-overlay');
    this.searchInput = this.element.querySelector('.js-Reader-searchInput');
    this.searchMenu = this.element.querySelector('.js-Reader-search');
    this.searchToggle = this.element.querySelector('.js-Reader-searchToggle');
    this.searchToggleIcon = this.element.querySelector(
      '.js-Reader-searchToggleIcon',
    );
    this._keyEventListener = this._escapeListener.bind(this);

    this.moreToggle.addEventListener('click', () => this.toggleFullNav());
    this.searchToggle.addEventListener('click', () => this.toggleSearch());
    this.overlay.addEventListener('click', () => this.toggleSearch(false));
    window.addEventListener('load', () => this._initHeadsUp());
  }

  /**
   * Toggle the visibility of the full navigation
   *
   * @param {boolean} [show] - Optionally force the menu to show or hide. If
   * omitted, it will be the opposite of the current state.
   */
  toggleFullNav(show) {
    // Is the full navigation currently shown?
    const isShown = this.element.classList.contains(this.fullNavShownClass);
    show = show === undefined ? !isShown : show;

    // Only proceed if the new state is different than the old one
    if (show === isShown) return;

    // Hide search before opening full navigation
    if (show) this.toggleSearch(false);

    // Toggle Reader class to show or hide full navigation
    this.element.classList.toggle(this.fullNavShownClass, show);

    // Enable or disable scrolling page content in the background
    this._disableScrolling(show);

    // Backup or Restore the currently active quicknav
    for (const quickNavLink of this.quickNavLinks) {
      if (show) {
        const attrAriaCurrent = quickNavLink.getAttribute('aria-current');
        if (attrAriaCurrent === 'page') {
          quickNavLink.dataset.current = attrAriaCurrent;
          quickNavLink.removeAttribute('aria-current');
        }
      } else {
        const attrDataCurrent = quickNavLink.dataset.current;
        if (attrDataCurrent === 'page') {
          quickNavLink.setAttribute('aria-current', attrDataCurrent);
          delete quickNavLink.dataset.current;
        }
      }
    }

    // Set aria-current for the More button
    this.moreToggle.setAttribute('aria-expanded', show);

    // Listen or stop listening for `esc` key depending on state
    document[`${show ? 'add' : 'remove'}EventListener`](
      'keydown',
      this._keyEventListener,
    );
  }

  /**
   * Toggle the visibility of the search menu.
   *
   * @param {boolean} [show] - Optionally force the menu to show or hide. If
   * omitted, it will be the opposite of the current state.
   */
  toggleSearch(show) {
    // Is the search menu currently shown?
    const isShown = this.element.classList.contains(this.searchShownClass);
    show = show === undefined ? !isShown : show;

    // Only proceed if the new state is different than the old one
    if (show === isShown) return;

    // Hide full navigation before opening search
    if (show) this.toggleFullNav(false);

    this._disableScrolling(show);
    this.element.classList.toggle(this.searchShownClass, show);
    this.searchToggle.setAttribute('aria-expanded', show);
    this.searchToggleIcon.classList.toggle(this.iconToggleClass, show);
    this.searchInput[show ? 'focus' : 'blur']();

    // Listen or stop listening for `esc` key depending on state
    document[`${show ? 'add' : 'remove'}EventListener`](
      'keydown',
      this._keyEventListener,
    );
  }

  /**
   *
   * @param {boolean} [lock] - Whether to disable or enable scrolling
   * page content in the background
   */
  _disableScrolling(lock) {
    if (lock) {
      scrollLock.disablePageScroll();
    } else {
      scrollLock.enablePageScroll();
    }
  }

  /**
   * Closes the search menu when the escape key is pressed
   *
   * @param {event:keydown} event
   */
  _escapeListener(event) {
    // IE expects `Esc`, not `Escape`
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.toggleSearch(false);
      this.toggleFullNav(false);
    }
  }

  /**
   * Start the "peekaboo nav" behavior. Works best if triggered once the whole
   * page has loaded in case that impacts the page scroll (for example, hash
   * URLs resolving).
   */
  _initHeadsUp() {
    headsUp({
      selector: this.mastheadSelector,
      hiddenHeaderClass: this.mastheadHiddenClass,
      debounce: 100,
    });
  }
}

export default Reader;
